import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-datenschutzerklaerung";
const TITLE = "Shopify Datenschutzerklärung einbinden und aktuell halten";
const DESCRIPTION =
  "Datenschutzerklärung in Shopify anlegen, im Footer und Checkout verlinken, Cookie-Banner und Tracking sauber abstimmen und bei jeder neuen App aktuell halten.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Die Datenschutzerklärung legen Sie in Shopify als eigene Seite an und verlinken sie im Footer sowie in den Shop-Richtlinien, damit auch der Checkout sie kennt. Entscheidend ist danach die Pflege: Jede neue App und jedes neue Tracking-Skript verarbeitet Daten und gehört in den Text. Die Inhalte selbst kommen von einem Anbieter für Rechtstexte oder einer Kanzlei.";

const faqs = [
  {
    q: "Wo binde ich die Datenschutzerklärung in Shopify ein?",
    a: "Als eigene Seite unter Onlineshop und Seiten, zusätzlich in den Shop-Richtlinien hinterlegt, damit sie im Checkout verlinkt ist, und als Menüpunkt im Footer.",
  },
  {
    q: "Brauche ich ein Cookie-Banner?",
    a: "Sobald Dienste geladen werden, die nicht technisch notwendig sind, ist eine Einwilligung erforderlich. Wichtig ist, dass die Skripte tatsächlich erst nach Zustimmung laden und nicht nur optisch ein Banner erscheint.",
  },
  {
    q: "Was muss ich bei neuen Apps beachten?",
    a: "Viele Apps verarbeiten personenbezogene Daten oder laden externe Skripte. Nach jeder Installation gehört geprüft, ob der Text ergänzt werden muss.",
  },
  {
    q: "Soll die Seite bei Google indexiert werden?",
    a: "Sie muss für Besucher leicht erreichbar sein. Ob sie indexiert wird, ist für die Rankings unerheblich, viele Shops schließen rechtliche Seiten aus dem Index aus.",
  },
  {
    q: "Schreiben Sie die Datenschutzerklärung?",
    a: "Nein. Die Texte kommen von einem spezialisierten Anbieter oder Ihrer Kanzlei. Ich sorge für die korrekte technische Einbindung, die Verlinkung und dafür, dass Tracking-Skripte erst nach Einwilligung laden.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-datenschutzerklaerung")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Einrichtung",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Einrichtung"
      title="Shopify Datenschutzerklärung: einbinden ist der einfache Teil"
      intro="Die Seite ist schnell angelegt. Anspruchsvoller ist, dass sie zu dem passt, was im Shop tatsächlich läuft: Apps, Tracking, Chat-Widgets und Zahlungsdienste verarbeiten Daten, und der Text muss das abbilden."
      source="ratgeber-shopify-datenschutz"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Seite anlegen und verlinken",
          p: [
            "Legen Sie die Datenschutzerklärung als eigene Seite an und hinterlegen Sie sie zusätzlich in den Shop-Richtlinien, damit der Checkout sie verlinkt. Ergänzen Sie einen Menüpunkt im Footer, damit sie von jeder Seite erreichbar ist.",
          ],
        },
        {
          h: "Tracking und Einwilligung",
          p: [
            "Skripte, die nicht technisch notwendig sind, dürfen erst nach Zustimmung laden. Häufig ist das Banner vorhanden, die Skripte starten aber trotzdem sofort, weil sie fest im Theme eingebaut wurden.",
            "Sauber ist die Einbindung über die Consent-Mechanik, sodass Zustimmung und Ladeverhalten tatsächlich zusammenhängen.",
          ],
        },
        {
          h: "Bei jeder neuen App nachziehen",
          p: [
            "Shops verändern sich laufend. Was heute stimmt, passt nach drei neuen Apps nicht mehr. Ein kurzer Prüfschritt bei jeder Installation hält den Aufwand klein.",
          ],
          list: [
            "Welche Daten verarbeitet die App?",
            "Lädt sie externe Skripte im Frontend?",
            "Braucht sie eine Einwilligung, bevor sie startet?",
            "Muss der Text um einen Abschnitt ergänzt werden?",
          ],
        },
        {
          h: "Zuständigkeit klar trennen",
          p: [
            "Rechtstexte gehören zu einem spezialisierten Anbieter oder in Ihre Kanzlei. Ich übernehme die technische Seite: Einbindung, Verlinkung, Consent-gesteuertes Laden der Skripte und eine Struktur, in der Aktualisierungen schnell gehen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-impressum", label: "Impressum einrichten" },
        { to: "/blog/shopify-rechtssicher-deutschland", label: "Shop rechtssicher betreiben" },
        { to: "/shopify-support", label: "Shopify Support" },
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung" },
        { to: "/kontakt", label: "Einbindung anfragen" },
      ]}
    />
  );
}
